import { computed, ref, watch, type Ref } from 'vue';

/**
 * Shared date-range UI for the orders / lab-orders views.
 *
 * Exposes five preset shortcuts + a "Custom range" option that reveals the
 * freeform DatePicker. Even in custom mode the picker is capped by
 * `minPickerDate` / `maxPickerDate` so the user can't reach past the server's
 * hard lookback cap.
 */

export type DateRangePresetKey = '90d' | '6m' | '1y' | '2y' | 'custom';

export interface DateRangePreset {
  key: DateRangePresetKey;
  label: string;
  /** Days back from today. `null` means freeform (custom range). */
  days: number | null;
}

const PRESETS: DateRangePreset[] = [
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '6m', label: 'Last 6 months', days: 182 },
  { key: '1y', label: 'Last 1 year', days: 365 },
  { key: '2y', label: 'Last 2 years', days: 730 },
  { key: 'custom', label: 'Custom range', days: null },
];

export interface UseDateRangePresetsOptions {
  /** Which preset starts selected. */
  defaultPreset?: DateRangePresetKey;
  /** Hard cap that mirrors the server-side max-lookback. */
  maxLookbackDays?: number;
}

export interface UseDateRangePresetsReturn {
  preset: Ref<DateRangePresetKey>;
  dateRange: Ref<[Date | null, Date | null]>;
  options: DateRangePreset[];
  isCustom: Ref<boolean>;
  /** Earliest selectable date — exposed as `min-date` on the picker. */
  minPickerDate: Ref<Date>;
  /** Today, exposed as `max-date` to prevent future-dated ranges. */
  maxPickerDate: Ref<Date>;
  /** Programmatic preset change (e.g. on mount with a route param). */
  applyPreset(key: DateRangePresetKey): void;
}

function startOfDayBack(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function useDateRangePresets(
  options: UseDateRangePresetsOptions = {},
): UseDateRangePresetsReturn {
  const defaultPreset = options.defaultPreset ?? '90d';
  const maxLookbackDays = options.maxLookbackDays ?? 365;

  const preset = ref<DateRangePresetKey>(defaultPreset);
  const isCustom = computed(() => preset.value === 'custom');

  const minPickerDate = computed(() => startOfDayBack(maxLookbackDays));
  const maxPickerDate = computed(() => new Date());

  function rangeForPreset(key: DateRangePresetKey): [Date | null, Date | null] {
    const found = PRESETS.find((p) => p.key === key);
    if (!found || found.days === null) return [null, null];
    return [startOfDayBack(found.days), new Date()];
  }

  const dateRange = ref<[Date | null, Date | null]>(rangeForPreset(defaultPreset));

  function applyPreset(key: DateRangePresetKey): void {
    preset.value = key;
  }

  // When the preset changes to a non-custom value, recompute the range.
  // When it changes to custom we leave the existing range in place so the
  // user can refine the dates they were just viewing, rather than jumping
  // back to a default.
  watch(preset, (next) => {
    if (next === 'custom') return;
    dateRange.value = rangeForPreset(next);
  });

  return {
    preset,
    dateRange,
    options: PRESETS,
    isCustom,
    minPickerDate,
    maxPickerDate,
    applyPreset,
  };
}
